/**
 * VÉRAULT Member Suite — AgreementsShelf (Wave 27.1 · Phase 4e).
 * ────────────────────────────────────────────────────────────────────
 * Review · e-sign · submit forms. Status chips: Signed · Pending
 * Signature · Submitted · Awaiting Review.
 *
 *   Clients act on agreements INSIDE the portal — never punted to
 *   email chains.
 */
import React, { useState } from "react";
import { motion } from "framer-motion";
import { FileSignature, Check, Clock, Download } from "lucide-react";
import AgreementSignModal from "./AgreementSignModal";

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

const STATUS_META = {
  signed: { label: "Signed", Icon: Check, tone: "text-[hsl(var(--accent-sage))] bg-[hsl(var(--accent-sage)/0.1)]" },
  acknowledged: { label: "Acknowledged", Icon: Check, tone: "text-[hsl(var(--accent-sage))] bg-[hsl(var(--accent-sage)/0.1)]" },
  pending: { label: "Pending signature", Icon: Clock, tone: "text-[hsl(var(--accent-gold))] bg-[hsl(var(--accent-gold)/0.1)]" },
  sent: { label: "Pending signature", Icon: Clock, tone: "text-[hsl(var(--accent-gold))] bg-[hsl(var(--accent-gold)/0.1)]" },
  awaiting_signature: { label: "Pending signature", Icon: Clock, tone: "text-[hsl(var(--accent-gold))] bg-[hsl(var(--accent-gold)/0.1)]" },
  submitted: { label: "Submitted", Icon: Check, tone: "text-[hsl(var(--foreground-muted))] bg-[hsl(var(--background-paper))]" },
  awaiting_review: { label: "Awaiting review", Icon: Clock, tone: "text-[hsl(var(--foreground-muted))] bg-[hsl(var(--background-paper))]" },
  draft: { label: "Pending signature", Icon: Clock, tone: "text-[hsl(var(--accent-gold))] bg-[hsl(var(--accent-gold)/0.1)]" },
};

function StatusChip({ status }) {
  const m = STATUS_META[(status || "").toLowerCase()] || STATUS_META.pending;
  const { Icon } = m;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-[0.18em] ${m.tone}`}>
      <Icon className="size-3" strokeWidth={1.5} />
      {m.label}
    </span>
  );
}

function AgreementCard({ a, onSignClick }) {
  const status = (a.status || "pending").toLowerCase();
  const isActionable = ["pending", "sent", "awaiting_signature", "draft"].includes(status);
  const ts = a.signed_at || a.updated_at || a.sent_at || a.created_at;
  const canDownload = ["signed", "acknowledged"].includes(status);

  return (
    <article
      className="rounded-md bg-white/65 backdrop-blur-[2px] border border-[rgba(0,0,0,0.06)] p-5 sm:p-6"
      data-testid={`agreement-card-${a.id}`}
      data-agreement-status={status}
    >
      <div className="flex items-start gap-4">
        <span className="inline-flex items-center justify-center size-9 rounded-full bg-[hsl(var(--background-paper))] flex-shrink-0">
          <FileSignature className="size-4 text-[hsl(var(--foreground-muted))]" strokeWidth={1.4} />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-[17px] sm:text-[18px] leading-tight tracking-tight">
            {a.title || a.agreement_type || "Agreement"}
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <StatusChip status={status} />
            {ts && (
              <span className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
                {status === "signed" ? "signed " : "updated "}
                {fmtDate(ts)}
              </span>
            )}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {isActionable && (
              <button
                type="button"
                onClick={() => onSignClick(a)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-mono text-[10px] uppercase tracking-[0.22em] bg-[hsl(var(--foreground))] text-[hsl(var(--background))] hover:bg-[hsl(var(--accent-gold))] transition-colors"
                data-testid={`agreement-review-${a.id}`}
              >
                Review &amp; sign
                <FileSignature className="size-3" strokeWidth={1.5} />
              </button>
            )}
            {canDownload && (
              <a
                href={`/api/client-portal/agreements/${a.id}/pdf`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-mono text-[10px] uppercase tracking-[0.22em] border border-[hsl(var(--border))] text-[hsl(var(--foreground-secondary))] hover:border-[hsl(var(--accent-gold))] hover:text-[hsl(var(--accent-gold))] transition-colors"
                data-testid={`agreement-download-${a.id}`}
              >
                Download PDF
                <Download className="size-3" strokeWidth={1.5} />
              </a>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

export default function AgreementsShelf({ items = [], onChanged }) {
  const [signing, setSigning] = useState(null);
  if (!items || items.length === 0) return null;
  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      id="agreements"
      data-testid="essentials-agreements"
      className="scroll-mt-24"
    >
      <header className="mb-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
          Agreements
        </p>
        <h2 className="mt-1.5 font-serif text-[22px] sm:text-[26px] leading-tight tracking-tight">
          Signed papers &amp; pending signatures.
        </h2>
      </header>
      <div className="space-y-3 sm:space-y-4">
        {items.map((a) => (
          <AgreementCard key={a.id} a={a} onSignClick={setSigning} />
        ))}
      </div>
      {signing && (
        <AgreementSignModal
          agreement={signing}
          onClose={() => setSigning(null)}
          onSigned={() => onChanged?.()}
        />
      )}
    </motion.section>
  );
}
